import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  ArrowLeft,
  Star,
  TrendingUp,
  Users,
  ThumbsUp,
  MessageSquare,
  Award,
  BarChart3
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminFeedbackAnalytics = ({ user }) => {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/admin/feedback-analytics`);
      setAnalytics(response.data);
    } catch (error) {
      console.error("Failed to load feedback analytics:", error);
      if (error.response?.status === 403) {
        toast.error("Admin access required");
        navigate("/dashboard");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${
              star <= rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"
            }`}
          />
        ))}
      </div>
    );
  };

  const formatMonth = (monthStr) => {
    // Convert "2026-09" to "Sep 2026"
    try {
      const [year, month] = monthStr.split('-');
      const date = new Date(year, parseInt(month) - 1);
      return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
    } catch {
      return monthStr;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading feedback analytics...</p>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="min-h-screen bg-background p-6">
        <Button variant="ghost" onClick={() => navigate("/admin")} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Admin
        </Button>
        <Card className="card-valor">
          <CardContent className="p-8 text-center">
            <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">No Feedback Data</h2>
            <p className="text-muted-foreground">
              Feedback analytics will appear here once veterans start submitting reviews.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { overview, rating_distribution, monthly_trends, recent_feedback, top_responders } = analytics;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/admin")}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>
                  Feedback Analytics
                </h1>
                <p className="text-sm text-muted-foreground">
                  Veteran satisfaction insights
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Overview Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="card-valor" data-testid="total-feedback-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <MessageSquare className="w-8 h-8 text-blue-500" />
                <span className="text-2xl font-bold">{overview.total_feedback}</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">Total Reviews</p>
            </CardContent>
          </Card>

          <Card className="card-valor" data-testid="avg-rating-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Star className="w-8 h-8 text-yellow-500 fill-yellow-500" />
                <span className="text-2xl font-bold">{overview.average_rating}</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">Avg Rating</p>
            </CardContent>
          </Card>

          <Card className="card-valor" data-testid="helpful-pct-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <ThumbsUp className="w-8 h-8 text-green-500" />
                <span className="text-2xl font-bold">{overview.helpful_percentage}%</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">Found Helpful</p>
            </CardContent>
          </Card>

          <Card className="card-valor" data-testid="five-star-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Award className="w-8 h-8 text-purple-500" />
                <span className="text-2xl font-bold">{overview.five_star_count}</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">5-Star Reviews</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Rating Distribution */}
          <Card className="card-valor" data-testid="rating-distribution-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary" />
                Rating Distribution
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[5, 4, 3, 2, 1].map((rating) => {
                  const count = rating_distribution[rating] || 0;
                  const percentage = overview.total_feedback > 0 
                    ? (count / overview.total_feedback * 100) 
                    : 0;
                  return (
                    <div key={rating} className="flex items-center gap-3">
                      <div className="flex items-center gap-1 w-20">
                        <span className="font-medium">{rating}</span>
                        <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      </div>
                      <div className="flex-1 h-4 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-yellow-400 rounded-full transition-all duration-500"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className="text-sm text-muted-foreground w-12 text-right">
                        {count}
                      </span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Monthly Trends */}
          <Card className="card-valor" data-testid="monthly-trends-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                Monthly Trends
              </CardTitle>
              <CardDescription>Last 6 months</CardDescription>
            </CardHeader>
            <CardContent>
              {monthly_trends.length > 0 ? (
                <div className="space-y-3">
                  {monthly_trends.map((month) => (
                    <div key={month.month} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div>
                        <p className="font-medium">{formatMonth(month.month)}</p>
                        <p className="text-sm text-muted-foreground">
                          {month.count} review{month.count !== 1 ? 's' : ''}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {renderStars(Math.round(month.avg_rating))}
                        <span className="font-semibold ml-2">{month.avg_rating}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">
                  No monthly data available yet
                </p>
              )}
            </CardContent>
          </Card>

          {/* Top Responders */}
          <Card className="card-valor" data-testid="top-responders-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                Top Rated Responders
              </CardTitle>
              <CardDescription>Minimum 3 reviews to qualify</CardDescription>
            </CardHeader>
            <CardContent>
              {top_responders.length > 0 ? (
                <div className="space-y-3">
                  {top_responders.slice(0, 5).map((responder, index) => (
                    <div 
                      key={responder.responder_id} 
                      className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                          index === 0 ? 'bg-yellow-100 text-yellow-700' :
                          index === 1 ? 'bg-gray-100 text-gray-700' :
                          index === 2 ? 'bg-amber-100 text-amber-700' :
                          'bg-muted text-muted-foreground'
                        }`}>
                          #{index + 1}
                        </div>
                        <div>
                          <p className="font-medium">{responder.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {responder.review_count} reviews
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                        <span className="font-bold text-lg">{responder.avg_rating}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">
                  No responders with enough reviews yet
                </p>
              )}
            </CardContent>
          </Card>

          {/* Recent Feedback */}
          <Card className="card-valor" data-testid="recent-feedback-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-primary" />
                Recent Feedback
              </CardTitle>
              <CardDescription>Latest veteran reviews</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[320px]">
                {recent_feedback.length > 0 ? (
                  <div className="space-y-4 pr-4">
                    {recent_feedback.map((fb) => (
                      <div 
                        key={fb.feedback_id} 
                        className="p-4 border border-border rounded-lg"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {renderStars(fb.rating)}
                            {fb.was_helpful && (
                              <Badge variant="outline" className="text-green-600 border-green-200 bg-green-50">
                                <ThumbsUp className="w-3 h-3 mr-1" />
                                Helpful
                              </Badge>
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {formatDate(fb.created_at)}
                          </span>
                        </div>
                        <p className="text-sm font-medium mb-1">
                          Responder: {fb.responder_name}
                        </p>
                        {fb.feedback ? (
                          <p className="text-sm text-muted-foreground italic">
                            "{fb.feedback}"
                          </p>
                        ) : (
                          <p className="text-sm text-muted-foreground">
                            No written feedback
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">
                    No feedback received yet
                  </p>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default AdminFeedbackAnalytics;
